import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, TrendingDown, Zap, Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/reduce-support-costs.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const ReduceCustomerSupportCosts = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.reduceCustomerSupportCosts.metaTitle')}</title>
        <meta name="description" content={t('blog.reduceCustomerSupportCosts.metaDescription')} />
        <meta name="keywords" content="reduce customer support costs, AI customer support savings, AI call center ROI, how to automate customer calls with AI, AI customer service automation benefits" />
        <link rel="canonical" href="https://call24x7.ai/blog/reduce-customer-support-costs-with-ai" />
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:title" content={t('blog.reduceCustomerSupportCosts.title')} />
        <meta property="article:published_time" content="2025-12-07" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('blog.reduceCustomerSupportCosts.title'),
            "description": t('blog.reduceCustomerSupportCosts.metaDescription'),
            "author": {"@type": "Organization", "name": "Call24x7.AI"},
            "publisher": {"@type": "Organization", "name": "Call24x7.AI"},
            "datePublished": "2025-12-07",
            "mainEntityOfPage": "https://call24x7.ai/blog/reduce-customer-support-costs-with-ai"
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.reduceCustomerSupportCosts.title')}
        category={t('blog.categories.roiSavings')}
        date="December 7, 2025"
        readTime={`6 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="Reduce customer support costs with AI"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.reduceCustomerSupportCosts.intro')}
        </p>

        <div className="grid md:grid-cols-3 gap-4 my-8">
          <Card className="p-4 text-center">
            <TrendingDown className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary">70%</div>
            <p className="text-sm text-muted-foreground">{t('blog.reduceCustomerSupportCosts.stat1')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Zap className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary">24/7</div>
            <p className="text-sm text-muted-foreground">{t('blog.reduceCustomerSupportCosts.stat2')}</p>
          </Card>
          <Card className="p-4 text-center">
            <Bot className="w-10 h-10 text-primary mx-auto mb-2" />
            <div className="text-3xl font-bold text-primary">80%</div>
            <p className="text-sm text-muted-foreground">{t('blog.reduceCustomerSupportCosts.stat3')}</p>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.strategy1Title')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.strategy1Desc')}</p>
        <div className="bg-muted/50 p-4 rounded-lg my-4">
          <p className="font-semibold">{t('blog.reduceCustomerSupportCosts.costImpact')}:</p>
          <p>{t('blog.reduceCustomerSupportCosts.strategy1Impact')}</p>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.strategy2Title')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.strategy2Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.strategy3Title')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.strategy3Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.strategy4Title')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.strategy4Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.strategy5Title')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.strategy5Desc')}</p>
        <div className="space-y-3 my-6">
          {[
            t('blog.reduceCustomerSupportCosts.handleTime1'),
            t('blog.reduceCustomerSupportCosts.handleTime2'),
            t('blog.reduceCustomerSupportCosts.handleTime3')
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.exampleTitle')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.exampleIntro')}</p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>{t('blog.reduceCustomerSupportCosts.exampleBefore')}</li>
          <li>{t('blog.reduceCustomerSupportCosts.exampleAfter')}</li>
          <li>{t('blog.reduceCustomerSupportCosts.exampleNewCost')}</li>
          <li><strong>{t('blog.reduceCustomerSupportCosts.exampleSavings')}</strong></li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.reduceCustomerSupportCosts.gettingStartedTitle')}</h2>
        <p>{t('blog.reduceCustomerSupportCosts.gettingStartedDesc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.reduceCustomerSupportCosts.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.reduceCustomerSupportCosts.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.reduceCustomerSupportCosts.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default ReduceCustomerSupportCosts;
